import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';

const CacheManagement = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [dataSources, setDataSources] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [search, setSearch] = useState('');
  const [clearingId, setClearingId] = useState(null);

  // Fetch data
  const fetchData = useCallback(async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);

      const [statsRes, dataSourcesRes] = await Promise.all([
        adminApi.getCacheStats(),
        adminApi.getCachedDataSources({ 
          page: pagination.page, 
          limit: pagination.limit,
          search: search || undefined
        })
      ]);

      setStats(statsRes.data);
      setDataSources(dataSourcesRes.data.dataSources || []);
      setPagination(prev => ({
        ...prev,
        ...dataSourcesRes.data.pagination
      }));
    } catch (err) {
      console.error('Failed to load cache data:', err);
      toast.error('Failed to load cache data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [pagination.page, pagination.limit, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Clear cache for a specific data source
  const handleClearCache = async (dataSource) => {
    if (!window.confirm(
      `Are you sure you want to clear the cache for "${dataSource.name}"?\n\nThis will require the user to re-upload the file before importing again.`
    )) {
      return;
    }

    try {
      setClearingId(dataSource._id);
      const response = await adminApi.clearDataSourceCache(dataSource._id);
      
      if (response.data.cacheCleared?.fileData) {
        toast.success(`Cache cleared for "${dataSource.name}"`);
      } else {
        toast.success('No cached data found (may have already expired)');
      }
      
      // Refresh data
      fetchData(true);
    } catch (err) {
      console.error('Failed to clear cache:', err);
      toast.error('Failed to clear cache');
    } finally {
      setClearingId(null);
    }
  };

  // Format file size
  const formatSize = (bytes) => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">
            Cache Management
          </h1>
          <p className="mt-1 text-surface-400">
            Manage Redis cache for file uploads (CSV/Excel data sources)
          </p>
        </div>
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="px-4 py-2 text-surface-300 hover:bg-surface-800 rounded-lg flex items-center gap-2 transition-colors"
        >
          <svg className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-surface-800 rounded-lg p-5 border border-surface-700">
          <h3 className="text-sm font-medium text-surface-400 mb-2">Total File Sources</h3>
          <p className="text-2xl font-bold text-surface-100">{stats?.totalFileSources || 0}</p>
          <p className="text-xs text-surface-500 mt-1">CSV & Excel data sources</p>
        </div>
        
        <div className="bg-surface-800 rounded-lg p-5 border border-surface-700">
          <h3 className="text-sm font-medium text-surface-400 mb-2">Cached</h3>
          <p className="text-2xl font-bold text-green-400">{stats?.cachedCount || 0}</p>
          <p className="text-xs text-surface-500 mt-1">With data in Redis</p>
        </div>
        
        <div className="bg-surface-800 rounded-lg p-5 border border-surface-700">
          <h3 className="text-sm font-medium text-surface-400 mb-2">Not Cached</h3>
          <p className="text-2xl font-bold text-surface-400">{stats?.uncachedCount || 0}</p>
          <p className="text-xs text-surface-500 mt-1">Requires re-upload</p>
        </div>
        
        <div className="bg-surface-800 rounded-lg p-5 border border-surface-700">
          <h3 className="text-sm font-medium text-surface-400 mb-2">Cache TTL</h3>
          <p className="text-2xl font-bold text-blue-400">2h</p>
          <p className="text-xs text-surface-500 mt-1">Auto-expires after 2 hours</p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="mb-6 p-4 bg-blue-900/20 border border-blue-800 rounded-lg">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-300">
            <p className="font-medium mb-1">When to clear cache:</p>
            <ul className="list-disc list-inside text-blue-400 space-y-1">
              <li>After deploying code changes that affect file parsing (e.g., Excel sanitization fixes)</li>
              <li>When an import keeps failing with the same cached data</li>
              <li>To force a user to re-upload their file with fresh data</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by data source name or file name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Data Sources Table */}
      <div className="bg-surface-800 rounded-lg border border-surface-700">
        <div className="px-6 py-4 border-b border-surface-700">
          <h2 className="text-lg font-semibold text-surface-100">
            File Data Sources ({pagination.total})
          </h2>
        </div>

        {dataSources.length === 0 ? (
          <div className="p-8 text-center text-surface-400">
            <svg className="w-12 h-12 mx-auto mb-4 text-surface-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>No file-based data sources found</p>
            <p className="text-sm mt-1">CSV and Excel data sources will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-surface-700">
              <thead className="bg-surface-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                    Data Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                    File Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                    Cache Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                    Last Updated
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-surface-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-700">
                {dataSources.map((ds) => (
                  <tr key={ds._id} className="hover:bg-surface-700/50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-surface-100">{ds.name}</p>
                        <p className="text-xs text-surface-500">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${
                            ds.type === 'excel' ? 'bg-green-900/50 text-green-400' : 'bg-blue-900/50 text-blue-400'
                          }`}>
                            {ds.type?.toUpperCase()}
                          </span>
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-surface-300">{ds.companyName}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <p className="text-surface-300 truncate max-w-[200px]" title={ds.fileInfo?.originalName}>
                          {ds.fileInfo?.originalName || 'N/A'}
                        </p>
                        <p className="text-xs text-surface-500">
                          {formatSize(ds.fileInfo?.size)}
                          {ds.fileInfo?.sheetName && ` • Sheet: ${ds.fileInfo.sheetName}`}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {ds.cache?.hasData ? (
                        <div>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-900/50 text-green-400">
                            <span className="w-2 h-2 rounded-full bg-green-400 mr-1.5 animate-pulse"></span>
                            Cached
                          </span>
                          <p className="text-xs text-surface-500 mt-1">
                            Expires in {ds.cache.expiresIn}
                          </p>
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-surface-700 text-surface-400">
                          <span className="w-2 h-2 rounded-full bg-surface-500 mr-1.5"></span>
                          Not Cached
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-surface-400">
                      {formatDate(ds.updatedAt)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {ds.cache?.hasData && (
                        <button
                          onClick={() => handleClearCache(ds)}
                          disabled={clearingId === ds._id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-400 hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {clearingId === ds._id ? (
                            <span className="animate-spin h-4 w-4 border-2 border-red-400 border-t-transparent rounded-full"></span>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          )}
                          Clear Cache
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-surface-700 flex items-center justify-between">
            <p className="text-sm text-surface-400">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-1 text-sm text-surface-300 hover:bg-surface-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-1 text-sm text-surface-300 hover:bg-surface-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CacheManagement;
